import { z } from 'zod';

export const CARRIER_COUNTRIES = [
  'PL',
  'IT',
  'GB',
  'FR',
  'BE',
  'NL',
  'LU',
  'ES',
  'PT',
  'AT',
  'HU',
  'SK',
  'CZ',
  'IM',
  'GG',
  'JE',
  'IE',
] as const;

export const WEIGHT_UNITS = ['KG', 'G', 'LB', 'OZ'] as const;

/** Physical point-type filter options for parcel-locker methods. */
export const POINT_TYPES = ['APM', 'PUDO'] as const;
/** Sentinel for the "both" option - Radix Select disallows an empty-string value. */
export const POINT_TYPE_BOTH = 'both';

export const NEARBY_POINTS_MIN = 1;
export const NEARBY_POINTS_MAX = 50;
export const NEARBY_POINTS_DEFAULT = 5;

/** Mirrors backend config carrier.limits.max_point_search_distance_km_max. */
export const POINT_SEARCH_DISTANCE_MIN = 1;
export const POINT_SEARCH_DISTANCE_MAX = 1000;

/** Empty input = null = no distance limit. */
const nullableSearchDistance = z
  .union([
    z
      .number()
      .int('Enter a whole number of kilometers')
      .min(
        POINT_SEARCH_DISTANCE_MIN,
        `Must be between ${POINT_SEARCH_DISTANCE_MIN} and ${POINT_SEARCH_DISTANCE_MAX} km`,
      )
      .max(
        POINT_SEARCH_DISTANCE_MAX,
        `Must be between ${POINT_SEARCH_DISTANCE_MIN} and ${POINT_SEARCH_DISTANCE_MAX} km`,
      ),
    z.nan(),
    z.undefined(),
    z.null(),
  ])
  .transform((v) => (v == null || Number.isNaN(v) ? null : v))
  .nullable()
  .optional();

export const marketFormSchema = z.object({
  countries: z.array(z.enum(CARRIER_COUNTRIES)).min(1, 'Select at least one country'),
  name: z.string().trim().min(1, 'Enter a market name').max(255),
});

export type MarketFormValues = z.infer<typeof marketFormSchema>;

export const defaultMarketForm: MarketFormValues = {
  countries: [],
  name: '',
};

const nullableAmount = z
  .union([z.number().min(0, 'Cannot be negative'), z.nan(), z.undefined(), z.null()])
  .transform((v) => (v == null || Number.isNaN(v) ? null : v))
  .nullable()
  .optional();

export const methodConfigSchema = z
  .object({
    flat_rate_price: z.number({ invalid_type_error: 'Enter a price' }).min(0, 'Cannot be negative'),
    free_shipping_threshold: nullableAmount,
    max_weight: nullableAmount,
    max_weight_unit: z.enum(WEIGHT_UNITS).nullable().optional(),
    // Only rendered (and sent) for point-selection methods; optional so a hidden
    // field can never fail validation for door methods.
    nearby_points_count: z.number().int().min(NEARBY_POINTS_MIN).max(NEARBY_POINTS_MAX).optional(),
    // Physical point-type filter (point methods only): null = both lockers + PUDO.
    point_type: z.enum(POINT_TYPES).nullable().optional(),
    // Market-scoped max point-search distance (point methods only; saved via the
    // market endpoint, not the carrier-service one). Null = no limit.
    max_point_search_distance_km: nullableSearchDistance,
    is_enabled: z.boolean(),
  })
  .refine((data) => (data.max_weight == null ? true : !!data.max_weight_unit), {
    message: 'Select a unit',
    path: ['max_weight_unit'],
  });

export type MethodConfigFormValues = z.infer<typeof methodConfigSchema>;
